import * as THREE from 'three';

const WORLD_CENTER_Z = -7000;
const WORLD_SIZE = 27000;

const smoothstep = (a, b, value) => {
  const x = THREE.MathUtils.clamp((value - a) / (b - a), 0, 1);
  return x * x * (3 - 2 * x);
};

function hash2(x, y) {
  let hash = Math.imul(x, 0x1f123bb5) ^ Math.imul(y, 0x5f356495);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x2c1b3c6d);
  hash ^= hash >>> 12;
  hash = Math.imul(hash, 0x297a2d39);
  hash ^= hash >>> 15;
  return (hash >>> 0) / 4294967295;
}

function valueNoise(x, y) {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy);
  const b = hash2(ix + 1, iy);
  const c = hash2(ix, iy + 1);
  const d = hash2(ix + 1, iy + 1);
  return THREE.MathUtils.lerp(
    THREE.MathUtils.lerp(a, b, ux),
    THREE.MathUtils.lerp(c, d, ux),
    uy
  );
}

function fbm(x, y) {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  for (let octave = 0; octave < 3; octave += 1) {
    value += valueNoise(x * frequency, y * frequency) * amplitude;
    frequency *= 2.03;
    amplitude *= 0.49;
  }
  return value;
}

function runwayBlend(x, z, centerZ, halfLength, halfWidth) {
  const edgeX = smoothstep(halfWidth, halfWidth + 125, Math.abs(x));
  const edgeZ = smoothstep(halfLength, halfLength + 180, Math.abs(z - centerZ));
  return Math.max(edgeX, edgeZ);
}

export function terrainHeight(x, z) {
  const broad = fbm(x * 0.00072 + 9.1, z * 0.00072 - 4.2);
  const detail = fbm(x * 0.0021 - 3.4, z * 0.0021 + 6.7);
  const ridge = 1 - Math.abs(fbm(x * 0.00048 + 31, z * 0.00048 - 17) * 2 - 1);
  const mountainMask = smoothstep(850, 5600, Math.abs(x));
  const valleyRise = smoothstep(1150, 4600, Math.abs(x));
  let height = 5 + broad * 48 + detail * 20;
  height += mountainMask * (ridge * ridge * 540 + broad * 180);
  height += valleyRise * 80;

  const takeoffBlend = runwayBlend(x, z, 0, 840, 92);
  height = THREE.MathUtils.lerp(0, height, takeoffBlend);
  const landingBlend = runwayBlend(x, z, -13800, 1280, 102);
  height = THREE.MathUtils.lerp(12, height, landingBlend);
  return height;
}

function seededRandom(seed = 92417) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function makeSkyTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 512;
  const context = canvas.getContext('2d');
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#061429');
  gradient.addColorStop(0.28, '#17517c');
  gradient.addColorStop(0.58, '#65a5c6');
  gradient.addColorStop(0.77, '#d5c6a7');
  gradient.addColorStop(1, '#726654');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const glow = context.createRadialGradient(760, 182, 8, 760, 182, 170);
  glow.addColorStop(0, 'rgba(255,249,214,0.95)');
  glow.addColorStop(0.12, 'rgba(255,210,141,0.45)');
  glow.addColorStop(1, 'rgba(255,190,110,0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeCloudTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  const random = seededRandom(71);
  for (let i = 0; i < 14; i += 1) {
    const x = 45 + random() * 165;
    const y = 48 + random() * 28;
    const radius = 22 + random() * 35;
    const glow = context.createRadialGradient(x, y, 1, x, y, radius);
    glow.addColorStop(0, 'rgba(255,255,255,0.78)');
    glow.addColorStop(0.45, 'rgba(244,249,255,0.52)');
    glow.addColorStop(1, 'rgba(225,238,247,0)');
    context.fillStyle = glow;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function addShadows(object) {
  object.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

export class World {
  constructor(scene, { lowPower = false } = {}) {
    this.scene = scene;
    this.lowPower = lowPower;
    this.rings = [];
    this.clouds = [];
    this.runwayLights = [];
    this.runwayLightMaterials = [];
    this.animationTime = 0;
    this.runwayUpdateAccumulator = 0;
    this.createAtmosphere();
    this.createTerrain();
    this.createWater();
    this.createVegetation();
    this.createRunway('takeoff', 0, 0, 1500, 0);
    this.createRunway('landing', 0, -13800, 2400, 12);
    this.createAirfieldDetails();
    this.createClouds();
  }

  createAtmosphere() {
    this.scene.background = new THREE.Color('#0c2740');
    this.scene.fog = new THREE.FogExp2('#7894a1', 0.00012);

    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(13000, this.lowPower ? 28 : 42, this.lowPower ? 14 : 22),
      new THREE.MeshBasicMaterial({ map: makeSkyTexture(), side: THREE.BackSide, fog: false })
    );
    sky.position.set(0, 900, WORLD_CENTER_Z);
    this.scene.add(sky);

    const hemi = new THREE.HemisphereLight('#cfe9ff', '#374029', 2.25);
    this.scene.add(hemi);
    const sun = new THREE.DirectionalLight('#fff1d2', 4.3);
    sun.position.set(-3200, 4800, 1600);
    sun.target.position.set(0, 0, -6500);
    sun.castShadow = !this.lowPower;
    sun.shadow.mapSize.set(this.lowPower ? 512 : 1536, this.lowPower ? 512 : 1536);
    sun.shadow.camera.left = -1800;
    sun.shadow.camera.right = 1800;
    sun.shadow.camera.top = 1800;
    sun.shadow.camera.bottom = -1800;
    sun.shadow.camera.far = 12000;
    sun.shadow.bias = -0.00015;
    this.scene.add(sun, sun.target);
    this.sun = sun;

    const sunDisc = new THREE.Mesh(
      new THREE.SphereGeometry(115, 24, 12),
      new THREE.MeshBasicMaterial({ color: '#fff3c4', fog: false })
    );
    sunDisc.position.set(-5200, 4200, -10800);
    this.scene.add(sunDisc);
  }

  createTerrain() {
    const segments = this.lowPower ? 72 : 156;
    const geometry = new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, segments, segments);
    geometry.rotateX(-Math.PI / 2);
    geometry.translate(0, 0, WORLD_CENTER_Z);
    const position = geometry.attributes.position;
    const colors = new Float32Array(position.count * 3);
    const low = new THREE.Color('#36533a');
    const meadow = new THREE.Color('#617044');
    const rock = new THREE.Color('#625d51');
    const snow = new THREE.Color('#c4c4b7');
    const color = new THREE.Color();

    for (let index = 0; index < position.count; index += 1) {
      const x = position.getX(index);
      const z = position.getZ(index);
      const y = terrainHeight(x, z);
      position.setY(index, y);
      const variation = valueNoise(x * 0.012, z * 0.012) * 0.11 - 0.055;
      if (y < 65) color.copy(low).lerp(meadow, smoothstep(12, 65, y));
      else if (y < 390) color.copy(meadow).lerp(rock, smoothstep(65, 390, y));
      else color.copy(rock).lerp(snow, smoothstep(390, 690, y));
      color.offsetHSL(variation, variation * 0.25, variation * 0.45);
      colors[index * 3] = color.r;
      colors[index * 3 + 1] = color.g;
      colors[index * 3 + 2] = color.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.computeVertexNormals();
    const terrain = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.96,
        metalness: 0.02,
        flatShading: false
      })
    );
    terrain.receiveShadow = true;
    this.scene.add(terrain);
    this.terrain = terrain;
  }

  createWater() {
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(32000, 32000),
      this.lowPower
        ? new THREE.MeshStandardMaterial({
          color: '#174e64', roughness: 0.3, metalness: 0.08,
          transparent: true, opacity: 0.94
        })
        : new THREE.MeshPhysicalMaterial({
          color: '#174e64', roughness: 0.19, metalness: 0.08,
          transmission: 0.08, transparent: true, opacity: 0.91,
          clearcoat: 1, clearcoatRoughness: 0.18
        })
    );
    water.rotation.x = -Math.PI / 2;
    water.position.set(0, -7, WORLD_CENTER_Z);
    water.receiveShadow = true;
    this.scene.add(water);
    this.water = water;
  }

  createVegetation() {
    const random = seededRandom(8071);
    const count = this.lowPower ? 300 : 1050;
    const trunkGeometry = new THREE.CylinderGeometry(0.6, 0.9, 5, 5);
    const crownGeometry = new THREE.ConeGeometry(3.6, 12, 7);
    const trunks = new THREE.InstancedMesh(
      trunkGeometry,
      new THREE.MeshStandardMaterial({ color: '#4a3826', roughness: 1 }),
      count
    );
    const crowns = new THREE.InstancedMesh(
      crownGeometry,
      new THREE.MeshStandardMaterial({ color: '#183f2e', roughness: 0.94 }),
      count
    );
    const dummy = new THREE.Object3D();
    let placed = 0;
    while (placed < count) {
      const x = (random() - 0.5) * 16500;
      const z = 2600 - random() * 21000;
      const y = terrainHeight(x, z);
      const runwayZone = Math.abs(x) < 210 && (
        Math.abs(z) < 1150 || Math.abs(z + 13800) < 1650
      );
      if (runwayZone || y < 18 || y > 390) continue;
      const scale = 0.55 + random() * 1.05;
      dummy.position.set(x, y + 2.5 * scale, z);
      dummy.rotation.y = random() * Math.PI * 2;
      dummy.scale.set(scale, scale, scale);
      dummy.updateMatrix();
      trunks.setMatrixAt(placed, dummy.matrix);
      dummy.position.y = y + 8.6 * scale;
      dummy.updateMatrix();
      crowns.setMatrixAt(placed, dummy.matrix);
      placed += 1;
    }
    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    trunks.castShadow = !this.lowPower;
    trunks.receiveShadow = true;
    crowns.castShadow = !this.lowPower;
    crowns.receiveShadow = true;
    this.scene.add(trunks, crowns);

    const rockCount = this.lowPower ? 80 : 320;
    const rocks = new THREE.InstancedMesh(
      new THREE.DodecahedronGeometry(4, 0),
      new THREE.MeshStandardMaterial({ color: '#69675f', roughness: 0.98 }),
      rockCount
    );
    for (let index = 0; index < rockCount; index += 1) {
      const x = (random() - 0.5) * 19000;
      const z = 3000 - random() * 22000;
      const y = terrainHeight(x, z);
      const scale = 0.4 + random() * 2.6;
      dummy.position.set(x, y + scale, z);
      dummy.rotation.set(random() * 0.4, random() * Math.PI, random() * 0.4);
      dummy.scale.set(scale * 1.5, scale, scale);
      dummy.updateMatrix();
      rocks.setMatrixAt(index, dummy.matrix);
    }
    rocks.instanceMatrix.needsUpdate = true;
    rocks.castShadow = !this.lowPower;
    rocks.receiveShadow = true;
    this.scene.add(rocks);
  }

  createRunway(id, x, z, length, elevation) {
    const group = new THREE.Group();
    group.name = `${id}-runway`;
    const tarmac = new THREE.Mesh(
      new THREE.BoxGeometry(130, 1.4, length),
      new THREE.MeshStandardMaterial({ color: '#24282a', roughness: 0.91 })
    );
    tarmac.position.y = elevation + 0.28;
    tarmac.receiveShadow = true;
    group.add(tarmac);

    const shoulderMaterial = new THREE.MeshStandardMaterial({ color: '#4a4d49', roughness: 1 });
    for (const side of [-1, 1]) {
      const shoulder = new THREE.Mesh(new THREE.BoxGeometry(13, 0.6, length), shoulderMaterial);
      shoulder.position.set(side * 71, elevation + 0.05, 0);
      shoulder.receiveShadow = true;
      group.add(shoulder);
    }

    const white = new THREE.MeshBasicMaterial({ color: '#f2efe2' });
    const centerOffsets = [];
    for (let offset = -length / 2 + 80; offset < length / 2 - 60; offset += 96) {
      centerOffsets.push(offset);
    }
    const dummy = new THREE.Object3D();
    const centerMarks = new THREE.InstancedMesh(
      new THREE.BoxGeometry(2.2, 0.12, 42),
      white,
      centerOffsets.length
    );
    centerOffsets.forEach((offset, index) => {
      dummy.position.set(0, elevation + 1.05, offset);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      centerMarks.setMatrixAt(index, dummy.matrix);
    });
    centerMarks.instanceMatrix.needsUpdate = true;
    group.add(centerMarks);

    const thresholdPositions = [];
    for (const thresholdZ of [-length / 2 + 28, length / 2 - 28]) {
      for (let stripe = -4; stripe <= 4; stripe += 1) {
        if (stripe === 0) continue;
        thresholdPositions.push([stripe * 10.2, thresholdZ]);
      }
    }
    const thresholdBars = new THREE.InstancedMesh(
      new THREE.BoxGeometry(7, 0.12, 34),
      white,
      thresholdPositions.length
    );
    thresholdPositions.forEach(([barX, barZ], index) => {
      dummy.position.set(barX, elevation + 1.05, barZ);
      dummy.updateMatrix();
      thresholdBars.setMatrixAt(index, dummy.matrix);
    });
    thresholdBars.instanceMatrix.needsUpdate = true;
    group.add(thresholdBars);

    const lightGeometry = new THREE.SphereGeometry(0.8, 8, 6);
    const lightMaterial = new THREE.MeshBasicMaterial({
      color: id === 'landing' ? '#d8fff3' : '#f6eec8'
    });
    const lightPositions = [];
    const lightStep = this.lowPower ? 96 : 58;
    for (let offset = -length / 2; offset <= length / 2; offset += lightStep) {
      for (const side of [-1, 1]) {
        lightPositions.push([side * 68, elevation + 1.35, offset, Math.abs(offset * 0.09) + side]);
      }
    }
    lightMaterial.transparent = true;
    const lights = new THREE.InstancedMesh(lightGeometry, lightMaterial, lightPositions.length);
    lightPositions.forEach(([lightX, lightY, lightZ], index) => {
      dummy.position.set(lightX, lightY, lightZ);
      dummy.updateMatrix();
      lights.setMatrixAt(index, dummy.matrix);
    });
    lights.instanceMatrix.needsUpdate = true;
    group.add(lights);
    this.runwayLightMaterials.push(lightMaterial);
    group.position.set(x, 0, z);
    this.scene.add(group);
  }

  createAirfieldDetails() {
    const hangarMaterial = new THREE.MeshStandardMaterial({
      color: '#748188', metalness: 0.52, roughness: 0.48
    });
    const glass = this.lowPower
      ? new THREE.MeshStandardMaterial({ color: '#17394d', roughness: 0.18, metalness: 0.2 })
      : new THREE.MeshPhysicalMaterial({
        color: '#17394d', roughness: 0.1, metalness: 0.2, transmission: 0.22
      });
    const createHangars = (centerZ, elevation, side) => {
      for (let index = 0; index < 4; index += 1) {
        const group = new THREE.Group();
        const building = new THREE.Mesh(new THREE.BoxGeometry(58, 22, 44), hangarMaterial);
        building.position.y = elevation + 11;
        const roof = new THREE.Mesh(
          new THREE.CylinderGeometry(29, 29, 44, 12, 1, false, 0, Math.PI),
          hangarMaterial
        );
        roof.rotation.x = Math.PI / 2;
        roof.rotation.z = Math.PI / 2;
        roof.position.y = elevation + 22;
        const door = new THREE.Mesh(new THREE.PlaneGeometry(42, 16), glass);
        door.position.set(0, elevation + 9, -22.1);
        group.add(building, roof, door);
        group.position.set(side * (130 + index * 72), 0, centerZ + (index % 2) * 65);
        addShadows(group);
        this.scene.add(group);
      }
    };
    createHangars(440, 0, 1);
    createHangars(-13520, 12, -1);

    const tower = new THREE.Group();
    const stem = new THREE.Mesh(
      new THREE.CylinderGeometry(8, 12, 48, 8),
      new THREE.MeshStandardMaterial({ color: '#616a6a', roughness: 0.78 })
    );
    stem.position.y = 24;
    const cabin = new THREE.Mesh(new THREE.CylinderGeometry(18, 14, 12, 8), glass);
    cabin.position.y = 53;
    const roof = new THREE.Mesh(
      new THREE.ConeGeometry(20, 6, 8),
      new THREE.MeshStandardMaterial({ color: '#b64832', roughness: 0.7 })
    );
    roof.position.y = 62;
    tower.add(stem, cabin, roof);
    tower.position.set(-170, 0, 350);
    addShadows(tower);
    this.scene.add(tower);
  }

  createClouds() {
    const texture = makeCloudTexture();
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.64,
      depthWrite: false,
      color: '#f1f6f7',
      fog: true,
      side: THREE.DoubleSide
    });
    const random = seededRandom(414);
    const count = this.lowPower ? 22 : 62;
    const geometry = new THREE.PlaneGeometry(1, 1);
    const clouds = new THREE.InstancedMesh(geometry, material, count);
    const dummy = new THREE.Object3D();
    for (let index = 0; index < count; index += 1) {
      const scale = 170 + random() * 420;
      dummy.position.set(
        (random() - 0.5) * 15000,
        430 + random() * 720,
        2500 - random() * 21000
      );
      dummy.rotation.set(0, (random() - 0.5) * 0.28, (random() - 0.5) * 0.12);
      dummy.scale.set(scale * (1.5 + random()), scale, 1);
      dummy.updateMatrix();
      clouds.setMatrixAt(index, dummy.matrix);
    }
    clouds.instanceMatrix.needsUpdate = true;
    clouds.frustumCulled = false;
    this.cloudField = clouds;
    this.scene.add(clouds);
  }

  querySurface(x, z) {
    const takeoff = Math.abs(x) <= 65 && z <= 760 && z >= -760;
    const landing = Math.abs(x) <= 65 && z <= -12610 && z >= -14990;
    return {
      groundHeight: terrainHeight(x, z),
      isRunway: takeoff || landing,
      runwayId: landing ? 'landing' : takeoff ? 'takeoff' : null
    };
  }

  update(_time, dt = 0) {
    this.animationTime += dt;
    this.water.position.y = -7 + Math.sin(this.animationTime * 0.32) * 0.12;
    if (this.cloudField) this.cloudField.position.x = Math.sin(this.animationTime * 0.006) * 260;
    this.runwayUpdateAccumulator += dt;
    if (this.runwayUpdateAccumulator >= 0.1) {
      this.runwayUpdateAccumulator %= 0.1;
      this.runwayLightMaterials.forEach((material, index) => {
        material.opacity = 0.72 + Math.sin(this.animationTime * 2.5 + index) * 0.22;
      });
    }
  }
}
